/**
 * <collection-infinite-scroll> — PLP infinite scroll.
 *
 * Wraps #ProductGridContainer. As a sentinel near the end of the grid enters the
 * viewport, the next page is fetched (section-only render) and its cards are
 * appended to #product-grid.
 *
 * NOTE: this is a separate element from Shopify's built-in <collection-component>
 * (Standard Events analytics, `view-event-payload`) — don't reuse that tag, its
 * definition already occupies the registry and would shadow ours.
 *
 * Plays nicely with Dawn's facets.js: every filter/sort change replaces
 * #ProductGridContainer's inner HTML (page 1 + a fresh trigger). We watch that
 * container with a MutationObserver (childList only — our own appends go deep
 * into the <ul>, so they never retrigger it) and rebind to the new trigger.
 *
 * Graceful degradation: the trigger is a real <a href="{next}">, so it works as
 * a plain link without JS and inside the theme editor (design mode).
 */

class CollectionInfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.isLoading = false;
    this.abortController = null; // for the in-flight next-page fetch

    // Preload a bit ahead (300px) rather than far ahead, so the load-more
    // spinner is actually visible when a page fetch takes real time.
    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) this.loadNext();
      },
      { rootMargin: '0px 0px 900px 0px' }
    );

    // Intercept clicks on the "Load more" link so it auto-loads in place.
    this.addEventListener('click', (event) => {
      const loadMoreLink = event.target.closest('[data-next-url]');
      if (!loadMoreLink) return;
      event.preventDefault();
      this.loadNext();
    });

    // Rebind whenever facets.js swaps out the grid container's contents.
    this.gridContainer = this.querySelector('#ProductGridContainer');
    if (this.gridContainer) {
      this.mutationObserver = new MutationObserver(() => this.bindTrigger());
      this.mutationObserver.observe(this.gridContainer, { childList: true });
    }

    this.bindTrigger();
  }

  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.abortController?.abort();
  }

  /** (Re)attach the intersection observer to the current trigger element. */
  bindTrigger() {
    this.intersectionObserver.disconnect();
    this.trigger = this.querySelector('[data-infinite-scroll]');
    // No trigger = no next page (or numbered-pagination mode). Nothing to watch.
    // Auto-load is off in the theme editor; the link still works on click.
    if (!this.trigger || window.Shopify?.designMode) return;
    this.intersectionObserver.observe(this.trigger);
  }

  async loadNext() {
    if (this.isLoading || !this.trigger) return;
    const loadMoreLink = this.trigger.querySelector('[data-next-url]');
    const nextUrl = loadMoreLink?.dataset.nextUrl;
    if (!nextUrl) return;

    this.isLoading = true;
    this.intersectionObserver.disconnect();
    this.trigger.classList.add('is-loading');
    this.abortController = new AbortController();

    try {
      const sectionId = this.querySelector('#product-grid')?.dataset.id;
      const url = new URL(nextUrl, window.location.origin);
      if (sectionId) url.searchParams.set('section_id', sectionId);

      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const parsedDocument = new DOMParser().parseFromString(await response.text(), 'text/html');

      const productGrid = this.querySelector('#product-grid');
      const newProductItems = parsedDocument.querySelectorAll('#product-grid > li');
      newProductItems.forEach((listItem) => productGrid.appendChild(document.importNode(listItem, true)));

      // Point the trigger at the following page, or retire it when done.
      const nextTrigger = parsedDocument.querySelector('[data-next-url]');
      if (nextTrigger) {
        loadMoreLink.dataset.nextUrl = nextTrigger.dataset.nextUrl;
        loadMoreLink.setAttribute('href', nextTrigger.getAttribute('href'));
        this.trigger.classList.remove('is-loading');
        this.isLoading = false;
        // Re-observe: if the sentinel is still in view (tall viewport / short
        // page), this fires again and keeps filling until it scrolls off.
        this.intersectionObserver.observe(this.trigger);
      } else {
        this.trigger.remove();
        this.trigger = null;
        this.isLoading = false;
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[collection-infinite-scroll] load-more failed:', error);
      this.trigger.classList.remove('is-loading');
      this.isLoading = false;
      // Leave the trigger as a working link so the user can retry / click through.
    }
  }
}

if (!customElements.get('collection-infinite-scroll')) {
  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
}
